import React, { useState, useEffect, useRef, useCallback } from 'react';
import { tokens } from '../design-system/tokens';

const T = {
  paper:     '#F7EFD8',
  paperDeep: '#EEE2BF',
  paperSoft: '#FDF7E5',
  card:      '#FFFBEC',
  ink:       '#2E2109',
  ink2:      '#4A3510',
  inkMuted:  '#6E5A38',
  inkFaint:  '#A39068',
  rule:      '#D7C9A1',
  ruleStrong:'#BFAB79',
  orange:    '#EA7C1F',
  orangeDark:'#C25F0E',
  moss:      '#6B7A3E',
  capRed:    '#C14A33',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

const DEFAULT_WIDTH = 450;
const MIN_WIDTH = 320;
const MAX_WIDTH = 800;
const MIN_MAIN_CONTENT = 400;
const STORAGE_KEY = 'chatPanelWidth';

/**
 * ChatPanel - Shared resizable, poppable chat wrapper component.
 *
 * Props:
 * - isOpen: boolean - controls visibility
 * - onClose: function - called when closing
 * - children: ReactNode - the PlanChatbot instance
 * - chatUrl: string - URL for pop-out window
 * - isMobile: boolean - disables resize, uses 100% width
 * - onWidthChange: function(width: number) - callback when width changes (for parent paddingRight)
 */
const ChatPanel = ({ isOpen, onClose, children, chatUrl, isMobile = false, onWidthChange }) => {
  const [width, setWidth] = useState(() => {
    if (isMobile) return window.innerWidth;
    const saved = localStorage.getItem(STORAGE_KEY);
    const parsed = saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
    return isNaN(parsed) ? DEFAULT_WIDTH : Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, parsed));
  });
  const isDragging = useRef(false);
  const panelRef = useRef(null);

  // Notify parent of width on mount and changes
  useEffect(() => {
    if (isOpen && onWidthChange) {
      onWidthChange(isMobile ? window.innerWidth : width);
    }
  }, [isOpen, width, isMobile, onWidthChange]);

  // Notify parent when closing
  useEffect(() => {
    if (!isOpen && onWidthChange) {
      onWidthChange(0);
    }
  }, [isOpen, onWidthChange]);

  // Drag resize handler
  const handleMouseDown = useCallback((e) => {
    if (isMobile) return;
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      requestAnimationFrame(() => {
        const newWidth = window.innerWidth - e.clientX;
        const clamped = Math.max(
          MIN_WIDTH,
          Math.min(MAX_WIDTH, newWidth, window.innerWidth - MIN_MAIN_CONTENT)
        );
        setWidth(clamped);
      });
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Save to localStorage on drag end
      setWidth((current) => {
        localStorage.setItem(STORAGE_KEY, String(current));
        return current;
      });
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isMobile]);

  // Pop-out handler
  const handlePopOut = useCallback(() => {
    if (!chatUrl) return;
    const popWidth = Math.max(width, 600);
    const popHeight = Math.min(window.innerHeight - 100, 900);
    const left = window.screenX + window.innerWidth - popWidth - 50;
    const top = window.screenY + 50;
    window.open(
      chatUrl,
      'cahoots-chat',
      `width=${popWidth},height=${popHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    onClose?.();
  }, [chatUrl, width, onClose]);

  if (!isOpen) return null;

  const panelWidth = isMobile ? '100%' : `${width}px`;

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: 60,
        right: 0,
        width: panelWidth,
        height: 'calc(100vh - 60px)',
        backgroundColor: T.paper,
        borderLeft: `1px dashed ${T.rule}`,
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: T.sans,
        boxShadow: '-4px 0 24px rgba(46,33,9,0.08)',
      }}
    >
      {/* Drag handle on left edge */}
      {!isMobile && (
        <div
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '6px',
            cursor: 'col-resize',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{
            width: '2px',
            height: '40px',
            backgroundColor: T.ruleStrong,
            borderRadius: '1px',
            transition: 'background-color 0.15s',
          }} />
        </div>
      )}

      {/* Toolbar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '4px 8px',
        borderBottom: `1px solid ${T.rule}`,
        backgroundColor: T.paperDeep,
        gap: '4px',
        minHeight: '36px',
        flexShrink: 0,
      }}>
        {/* Pop-out button */}
        {!isMobile && chatUrl && (
          <button
            onClick={handlePopOut}
            title="Open in new window"
            style={toolbarButtonStyle}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.inkMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </button>
        )}
        {/* Close button */}
        <button
          onClick={onClose}
          title="Close chat"
          style={toolbarButtonStyle}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.inkMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Chat content */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );
};

const toolbarButtonStyle = {
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  color: T.inkMuted,
  border: `1px solid ${T.rule}`,
  borderRadius: T.r2,
  cursor: 'pointer',
  padding: 0,
  transition: 'all 0.15s',
};

export default ChatPanel;
